import { useEffect, useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { useEvents, usePublicData } from '../lib/hooks';
import { errorText } from '../lib/errors';
import { KIND_LABEL, mealName, serveDateText, windowText } from '../lib/format';
import type { Kind, Sitting } from '../lib/types';
import { LOGO_SRC, Spinner, VegMark } from '../components/ui';
import { LiveStrip } from '../components/live';
import { foodVenues } from '../lib/live';

const KIND_ORDER: Record<Kind, number> = { veg: 0, nonveg: 1, common: 2 };

/** "Where to eat": each hall, where it is, and what is served there on each day. No sign-in needed. */
export default function Venues() {
  const { session } = useAuth();
  const { menu, venues } = usePublicData();
  const ev = useEvents();
  const sittings = ev.current;
  const { hash } = useLocation();
  const ready = ev.ready && !!menu.data && !!venues.data;

  const schedule = useMemo(() => {
    const out = new Map<string, { day: string; bn: string; date: string; meals: { s: Sitting; kinds: Kind[] }[] }[]>();
    for (const v of venues.data ?? []) {
      const days: { day: string; bn: string; date: string; meals: { s: Sitting; kinds: Kind[] }[] }[] = [];
      for (const s of sittings) {
        const kinds = (menu.data ?? []).filter((m) => m.sitting_id === s.id && m.venue_id === v.id)
          .map((m) => m.kind).sort((a, b) => KIND_ORDER[a] - KIND_ORDER[b]);
        if (!kinds.length) continue;
        const last = days[days.length - 1];
        if (last && last.day === s.day_label) last.meals.push({ s, kinds });
        else days.push({ day: s.day_label, bn: s.day_label_bn, date: serveDateText(s), meals: [{ s, kinds }] });
      }
      out.set(v.id, days);
    }
    return out;
  }, [sittings, menu.data, venues.data]);

  // Venue chips link to /venues#t6-banquet.
  useEffect(() => {
    if (!ready || !hash) return;
    document.getElementById(hash.slice(1))?.scrollIntoView({ block: 'start' });
  }, [ready, hash]);

  return (
    <div className="shell">
      {session ? (
        <div className="app-bar slim"><Link className="link-btn" to="/">‹ My passes</Link><span className="app-sub">Where to eat</span></div>
      ) : (
        <div className="app-bar">
          <div className="pub-head">
            <span className="brand-chip" style={{ justifySelf: 'start' }}><img src={LOGO_SRC} alt="ONE10" style={{ height: 32 }} /></span>
            <p className="app-eyebrow">{ev.live.length ? `${ev.live.map((e) => e.name).join(' · ')} · ` : ''}Food</p>
          </div>
        </div>
      )}
      <main className="app-body">
        <div className="pass-head" style={{ textAlign: 'left' }}>
          <p className="app-eyebrow">কোথায় খাবেন</p>
          <h1 className="pass-title" style={{ fontSize: 26 }}>Where to eat</h1>
          <p className="app-sub">Veg and non-veg are served in different halls. Your pass works in both.</p>
        </div>
        <LiveStrip />
        {!ready ? (menu.isError || venues.isError
          ? <p className="err">{errorText(menu.error ?? venues.error)}</p>
          : <div className="empty"><Spinner /></div>) : foodVenues(venues.data ?? [], menu.data ?? []).map((v) => {
          const days = schedule.get(v.id) ?? [];
          return (
            <section key={v.id} id={v.id} className={`venue${hash === `#${v.id}` ? ' is-target' : ''}`}>
              <div className="venue-head">
                {v.name_bn && <p className="venue-bn">{v.name_bn}</p>}
                <h2 className="venue-name">{v.name}</h2>
                {v.where_text && <p className="venue-where">{v.where_text}</p>}
              </div>
              {v.notes && <p className="venue-notes">{v.notes}</p>}
              {days.length === 0 ? <p className="fine">No meals here this year.</p> : (
                <ul className="venue-days">
                  {days.map((d) => (
                    <li key={d.day}>
                      <p className="pday-h"><span className="bn">{d.bn}</span>{d.day}<span className="pday-date">{d.date}</span></p>
                      <ul className="venue-meals">
                        {d.meals.map(({ s, kinds }) => (
                          <li key={s.id}>
                            <span className="venue-meal">{mealName(s)}</span>
                            <span className="venue-kinds">{kinds.map((k) => <span key={k}><VegMark kind={k} />{KIND_LABEL[k]}</span>)}</span>
                            <span className="venue-win">{windowText(s)}</span>
                          </li>
                        ))}
                      </ul>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          );
        })}
        <p className="fine">Show your family pass at the counter in the hall for your food. Families with veg and non-veg seats can use the same QR in both halls.</p>
        {!session && <p className="fine"><Link to="/signin">Sign in</Link> to book meals and see your passes.</p>}
      </main>
    </div>
  );
}
